import {ValidationResult, Validator} from 'common-utils/interfaces';
import {allInputTagsSelector} from './pds-abstract-input-widget';

export let pdsValidation = {
  pds: undefined,
  _customValidationFunctions: {},
  /**
   * Searches the DOM element for all descendants that are widgets that can be validated
   * and runs their validation functions which will put any error messages up on the screen
   * as well as return whether all elements are valid within the container or not.
   * @param element: a DOM element
   * @returns true if all the elements are valid
   *          false if one or more elements are NOT valid
   */
  validateElements: function (element: HTMLElement, setFocus = true): boolean {
    if (element == null) {
      return true;
    }

    let allElementsAreValid = true;

    let inputWidgets = element.querySelectorAll(allInputTagsSelector);
    for (let i = 0; i < inputWidgets.length; i++) {
      //It's important that the AND allElementsAreValid is tested AFTER numWidgets[i].validateNumber
      //so ALL elements will be validated.
      //The parameter to validateWidget here could come from parameters of this method to enable us
      //to tell the widget whether or not it should show errors after validation.
      allElementsAreValid = (<any>inputWidgets[i]).validateWidget(true) && allElementsAreValid;
    }
    // if there are invalid elements, then set focus on the first invalid element
    if (!allElementsAreValid && setFocus) {
      this.setFocusOnFirstInvalidElement(element);
    }
    return allElementsAreValid;

  },
  /**
   * Registers a function to be used as a validator for validation widgets.
   * @param functionName: the key by which we will look up this function. See getCustomValidate for more info.
   * @param executeFunction: the function that will do the validation. Must meet the following interface:
   * (value: string, condition: any, context: {[key: string]: any}) => ValidationResult
   * NOTE: condition will be { element: PdsAbstractInputWidget } which allows the validate function
   * to get at any other special data attached to the widget element.
   */
  registerCustomValidate: function(functionName: string, executeFunction: (value: string, condition: any, context: {[key: string]: any}) => ValidationResult): void {
    if (functionName == null || functionName === '' || executeFunction == null) {
      return;
    }
    this._customValidationFunctions[functionName] = executeFunction;
  },
  /**
   * Looks up a validator function that was previously registered by registerCustomValidate. This validator is used
   * by validation widgets. Being able to implement custom validators adds flexibility to the widgets.
   * @param functionName: the name used to register the validator in registerCustomValidate
   * @returns {any}: a function used as a validator.
   */
  getCustomValidate: function(functionName: string): (value: string, condition: any, context: {[key: string]: any}) => ValidationResult {
    return this._customValidationFunctions[functionName];
  },

  /**
   * This function returns a list of invalid elements that are contained within some parent element.
   * @param page
   * @return list of invalid elements
   */

  getAllInvalidElements(parentElement: HTMLElement): Array<HTMLElement> {
    let myInvalidElements = new Array<HTMLElement>();
    let allMyChildInputElements = parentElement.querySelectorAll(allInputTagsSelector);
    for (let i = 0; i < allMyChildInputElements.length; i++) {
      if (!(<any>allMyChildInputElements[i]).validateWidget(true)) {
        myInvalidElements.push(<HTMLElement>allMyChildInputElements[i]);
      }
    }
    return myInvalidElements;
  },

  /**
   * This function returns the first invalid element on the page
   * @param page
   * @return list of invalid elements
   */

  getFirstInvalidElement(parentElement: HTMLElement): HTMLElement {
    let myFirstInvalidElement;
    let allMyChildInputElements = parentElement.querySelectorAll(allInputTagsSelector);
    for (let i = 0; i < allMyChildInputElements.length; i++) {
      if (!(<any>allMyChildInputElements[i]).validateWidget(true)) {
        myFirstInvalidElement = <HTMLElement>allMyChildInputElements[i];
        return myFirstInvalidElement;
      }
    }
    return myFirstInvalidElement;
  },

  /**
   * sets focus on the first invalid element
   */

  setFocusOnFirstInvalidElement(parentElement: HTMLElement): void {
    let myFirstInvalidElement = this.getFirstInvalidElement(parentElement);
    if (myFirstInvalidElement != null) {
      myFirstInvalidElement.setAttribute('tabindex', '-1');
      myFirstInvalidElement.focus();
    }
  }
};
